// @spec BCP-STR-019
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompleteReview, useReviewContext, useStartReview } from '@/features/bcp/api/planReviewApi';
import { PlanReviewForm } from '@/features/bcp/components/PlanReviewForm';
import type { CompleteReviewRequest } from '@/features/bcp/types';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/bcp/plans/$planId/review')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.reviewPlan'),
  }),
  component: PlanReviewPage,
});

function PlanReviewPage() {
  const { t } = useTranslation('bcp');
  const { planId } = Route.useParams();
  const navigate = useNavigate();
  const { data: reviewContext, isLoading, isError } = useReviewContext(planId);
  const startReview = useStartReview(planId);
  const completeReview = useCompleteReview(planId);
  const [reviewId, setReviewId] = React.useState<string | null>(null);
  const [autoStarted, setAutoStarted] = React.useState(false);

  // Auto-create review if none active
  React.useEffect(() => {
    if (reviewContext && !reviewContext.activeReview && !autoStarted && !startReview.isPending) {
      setAutoStarted(true);
      startReview.mutate(
        { reviewType: 'scheduled' },
        {
          onSuccess: (data) => {
            setReviewId(data.id);
          },
          onError: () => {
            toast.error(t('bcp:toast.failedToStartReviewThePlanMayNotBeInAReviewableState'));
          },
        },
      );
    } else if (reviewContext?.activeReview) {
      setReviewId(reviewContext.activeReview.id);
    }
  }, [reviewContext, autoStarted, startReview]);

  const handleComplete = (data: CompleteReviewRequest) => {
    if (!reviewId) return;
    completeReview.mutate(
      { reviewId, data },
      {
        onSuccess: () => {
          toast.success(
            data.outcome === 'no_changes'
              ? t('bcp:routes.planRecertified')
              : t('bcp:routes.planReturnedToDraft'),
          );
          void navigate({ to: '/bcp/plans/$planId', params: { planId } });
        },
        onError: () => {
          toast.error(t('bcp:toast.failedToCompleteReviewPleaseTryAgain'));
        },
      },
    );
  };

  const handleCancel = () => {
    void navigate({ to: '/bcp/plans/$planId', params: { planId } });
  };

  if (isLoading || startReview.isPending) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !reviewContext) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-destructive">{t('bcp:routes.failedToLoadReviewContext')}</p>
          <Button variant="outline" className="mt-4" onClick={handleCancel}>
            {t('bcp:routes.backToPlan')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Breadcrumb / Back nav */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('bcp:routes.backToPlan')}
          </Button>
          <span className="text-muted-foreground">/</span>
          <span className="text-sm text-muted-foreground">
            BCP &gt; Plans &gt; {reviewContext.planName} &gt; Review
          </span>
        </div>

        <h1 className="text-2xl font-bold">Conduct Plan Review</h1>

        {reviewId && (
          <PlanReviewForm
            planId={planId}
            reviewId={reviewId}
            reviewContext={reviewContext}
            onComplete={handleCancel}
            onCancel={handleCancel}
            onSubmit={handleComplete}
            isSubmitting={completeReview.isPending}
          />
        )}
      </div>
    </div>
  );
}
